import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { 
  ShieldCheck, 
  Search, 
  SlidersHorizontal, 
  MapPin, 
  Phone, 
  Mail, 
  Calendar, 
  CircleDollarSign, 
  ClipboardList, 
  CheckCircle2, 
  XCircle,
  Truck,
  RotateCcw,
  User,
  Package,
  Plus,
  X,
  Upload,
  Link,
  Edit
} from "lucide-react";

export default function AdminOrders() {
  const { user, isAuthenticated, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  // Tab Navigation State
  const [activeTab, setActiveTab] = useState("orders"); // "orders" or "products"

  // Product management states
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [productFilterCategory, setProductFilterCategory] = useState("all");
  const [savingProductId, setSavingProductId] = useState(null);

  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [submittingNewProduct, setSubmittingNewProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: "",
    category: "medicine",
    sub_category: "",
    short_description: "",
    description: "",
    price: "",
    imageType: "url",
    imageUrl: "",
    imageFileBase64: "",
    benefits: [""],
    in_stock: "10" // default stock count
  });

  const [isEditingProduct, setIsEditingProduct] = useState(false);
  const [submittingEditProduct, setSubmittingEditProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState({
    id: "",
    name: "",
    category: "medicine",
    sub_category: "",
    short_description: "",
    description: "",
    price: "",
    imageType: "url",
    imageUrl: "",
    imageFileBase64: "",
    benefits: [""],
    in_stock: "10"
  });

  // Authenticate staff access
  useEffect(() => {
    if (!loading && (!isAuthenticated || !isAdmin)) {
      toast.error("Restricted access. Please authenticate as a seller.");
      navigate("/admin/login");
    }
  }, [isAuthenticated, isAdmin, loading, navigate]);

  // Fetch all orders
  const fetchAllOrders = async () => {
    setLoadingOrders(true);
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } catch (err) {
      toast.error("Could not retrieve administrative order ledgers.");
    } finally {
      setLoadingOrders(false);
    }
  };

  // Fetch all products
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const { data } = await api.get("/products");
      setProducts(data);
    } catch (err) {
      toast.error("Failed to load products inventory.");
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      fetchAllOrders();
      fetchProducts();
    }
  }, [isAuthenticated, isAdmin]);

  // Submit handler for adding a new product
  const handleAddProductSubmit = async (e) => {
    e.preventDefault();
    if (!newProduct.name || !newProduct.category || !newProduct.short_description || !newProduct.description || !newProduct.price) {
      toast.error("Please fill in all required fields.");
      return;
    }

    const img = newProduct.imageType === "url" ? newProduct.imageUrl : newProduct.imageFileBase64;
    if (!img) {
      toast.error("Please provide a product image (URL or file).");
      return;
    }

    setSubmittingNewProduct(true);
    try {
      const payload = {
        name: newProduct.name,
        category: newProduct.category,
        sub_category: newProduct.sub_category || null,
        short_description: newProduct.short_description,
        description: newProduct.description,
        price: parseFloat(newProduct.price),
        image: img,
        benefits: newProduct.benefits.filter(b => b.trim() !== ""),
        in_stock: parseInt(newProduct.in_stock, 10) || 0
      };

      await api.post("/admin/products", payload);
      toast.success(`Created product ${payload.name} successfully.`);
      
      // Re-fetch products instantly to update the view without page refresh
      await fetchProducts();
      
      // Reset form & close
      setNewProduct({
        name: "",
        category: "medicine",
        sub_category: "",
        short_description: "",
        description: "",
        price: "",
        imageType: "url",
        imageUrl: "",
        imageFileBase64: "",
        benefits: [""],
        in_stock: "10"
      });
      setIsAddingProduct(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create product.");
    } finally {
      setSubmittingNewProduct(false);
    }
  };

  // Submit handler for editing an existing product
  const handleEditProductSubmit = async (e) => {
    e.preventDefault();
    if (!editingProduct.name || !editingProduct.category || !editingProduct.short_description || !editingProduct.description || !editingProduct.price) {
      toast.error("Please fill in all required fields.");
      return;
    }

    const img = editingProduct.imageType === "url" ? editingProduct.imageUrl : editingProduct.imageFileBase64;
    if (!img) {
      toast.error("Please provide a product image (URL or file).");
      return;
    }

    setSubmittingEditProduct(true);
    try {
      const payload = {
        name: editingProduct.name,
        category: editingProduct.category,
        sub_category: editingProduct.sub_category || null,
        short_description: editingProduct.short_description,
        description: editingProduct.description,
        price: parseFloat(editingProduct.price),
        image: img,
        benefits: editingProduct.benefits.filter(b => b.trim() !== ""),
        in_stock: parseInt(editingProduct.in_stock, 10) || 0
      };

      await api.put(`/admin/products/${editingProduct.id}`, payload);
      toast.success(`Updated product ${editingProduct.name} successfully.`);
      
      // Re-fetch products instantly
      await fetchProducts();
      setIsEditingProduct(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update product.");
    } finally {
      setSubmittingEditProduct(false);
    }
  };

  const handlePriceChange = (productId, val) => {
    setProducts(prev => prev.map(p => p.id === productId ? { ...p, price: val } : p));
  };

  const handleStockChange = (productId, val) => {
    setProducts(prev => prev.map(p => p.id === productId ? { ...p, in_stock: val } : p));
  };

  const saveProductChanges = async (product) => {
    const parsedPrice = parseFloat(product.price);
    if (isNaN(parsedPrice) || parsedPrice < 0) {
      toast.error("Please enter a valid price.");
      return;
    }

    const parsedStock = parseInt(product.in_stock, 10);
    if (isNaN(parsedStock) || parsedStock < 0) {
      toast.error("Please enter a valid stock quantity.");
      return;
    }
    
    setSavingProductId(product.id);
    try {
      await api.put(`/admin/products/${product.id}`, {
        price: parsedPrice,
        in_stock: parsedStock
      });
      toast.success(`Saved changes for ${product.name}.`);
      
      // Re-fetch products instantly to update the view
      await fetchProducts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save product changes.");
    } finally {
      setSavingProductId(null);
    }
  };

  // Update order status handler
  const updateStatus = async (orderId, newStatus) => {
    setUpdatingId(orderId);
    try {
      await api.put(`/admin/orders/${orderId}`, { status: newStatus });
      toast.success(`Order status updated to ${newStatus.replace("_", " ")}.`);
      
      // Update local state instantly
      setOrders((prev) => 
        prev.map((o) => o.id === orderId ? { ...o, status: newStatus } : o)
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update status.");
    } finally {
      setUpdatingId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "inquiry_received":
        return { label: "Inquiry Received", bg: "bg-[#FAF1EC]", text: "text-[#C86D4B]", border: "border-[#FAF1EC]" };
      case "confirmed":
        return { label: "Confirmed", bg: "bg-[#EAF2EC]", text: "text-[#3A5A40]", border: "border-[#EAF2EC]" };
      case "shipped":
        return { label: "Shipped", bg: "bg-[#EEF1F6]", text: "text-[#3B6088]", border: "border-[#EEF1F6]" };
      case "completed":
        return { label: "Completed", bg: "bg-[#F7F5E8]", text: "text-[#706D34]", border: "border-[#F7F5E8]" };
      case "cancelled":
        return { label: "Cancelled", bg: "bg-[#FAEAF0]", text: "text-[#A8324A]", border: "border-[#FAEAF0]" };
      default:
        return { label: status, bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-100" };
    }
  };

  // Metrics calculations
  const totalInquiries = orders.filter((o) => o.status === "inquiry_received").length;
  const totalConfirmed = orders.filter((o) => o.status === "confirmed" || o.status === "shipped").length;
  const completedRevenue = orders
    .filter((o) => o.status === "completed")
    .reduce((sum, o) => sum + o.total, 0);

  // Filtering + Searching Logic
  const filteredOrders = orders.filter((order) => {
    const matchesStatus = filterStatus === "all" || order.status === filterStatus;
    const matchesSearch = 
      order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.city && order.city.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesStatus && matchesSearch;
  });

  // Products filtering + searching
  const filteredProducts = products.filter((p) => {
    const matchesCategory = productFilterCategory === "all" || p.category === productFilterCategory;
    const matchesSearch = p.name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
      (p.sub_category && p.sub_category.toLowerCase().includes(productSearchTerm.toLowerCase())) ||
      p.short_description.toLowerCase().includes(productSearchTerm.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  if (loading || !isAuthenticated || !isAdmin) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: "var(--ayu-secondary) transparent transparent transparent" }}></div>
        <p className="mt-4 text-sm tracking-wide text-[var(--ayu-muted)]">Verifying staff clearance...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-orders-page" className="px-6 md:px-12 lg:px-20 pt-24 pb-24 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12 pb-8 border-b border-[var(--ayu-border)]">
        <div>
          <span className="overline flex items-center gap-1.5 mb-1.5">
            <ShieldCheck size={14} className="text-[var(--ayu-secondary)]" />
            <span>Administrative Board</span>
          </span>
          <h1 className="font-display text-4xl text-[var(--ayu-heading)]">Apothecary Fulfilment</h1>
        </div>
        <button 
          onClick={activeTab === "orders" ? fetchAllOrders : fetchProducts}
          className="btn-secondary text-xs py-2 px-4 border border-[var(--ayu-primary)] text-[var(--ayu-primary)] self-start md:self-center"
        >
          {activeTab === "orders" ? "Refresh Ledger" : "Refresh Inventory"}
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-[var(--ayu-border)] mb-8">
        <button
          onClick={() => setActiveTab("orders")}
          className={`flex items-center gap-2 px-6 py-3 font-display text-lg tracking-wide border-b-2 transition-all ${
            activeTab === "orders"
              ? "border-[var(--ayu-primary)] text-[var(--ayu-primary)] font-medium"
              : "border-transparent text-[var(--ayu-muted)] hover:text-[var(--ayu-heading)]"
          }`}
        >
          <ClipboardList size={18} />
          Orders Fulfilment
        </button>
        <button
          onClick={() => setActiveTab("products")}
          className={`flex items-center gap-2 px-6 py-3 font-display text-lg tracking-wide border-b-2 transition-all ${
            activeTab === "products"
              ? "border-[var(--ayu-primary)] text-[var(--ayu-primary)] font-medium"
              : "border-transparent text-[var(--ayu-muted)] hover:text-[var(--ayu-heading)]"
          }`}
        >
          <Package size={18} />
          Inventory Management
        </button>
      </div>

      {activeTab === "orders" && (
        <>
          {/* Metrics Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-[var(--ayu-surface)] grain p-6 border border-[var(--ayu-border)] flex items-center gap-4 relative overflow-hidden">
              <div className="p-3 bg-white/60 text-[var(--ayu-secondary)]">
                <ClipboardList size={22} strokeWidth={1.5} />
              </div>
              <div>
                <p className="overline text-[10px]" style={{ color: "var(--ayu-muted)" }}>Pending Enquiries</p>
                <p className="text-3xl font-display font-semibold text-[var(--ayu-heading)]">{totalInquiries}</p>
              </div>
            </div>

            <div className="bg-[var(--ayu-surface)] grain p-6 border border-[var(--ayu-border)] flex items-center gap-4 relative overflow-hidden">
              <div className="p-3 bg-white/60 text-[var(--ayu-primary)]">
                <Truck size={22} strokeWidth={1.5} />
              </div>
              <div>
                <p className="overline text-[10px]" style={{ color: "var(--ayu-muted)" }}>Active Shipments / Confirmed</p>
                <p className="text-3xl font-display font-semibold text-[var(--ayu-heading)]">{totalConfirmed}</p>
              </div>
            </div>

            <div className="bg-[var(--ayu-surface)] grain p-6 border border-[var(--ayu-border)] flex items-center gap-4 relative overflow-hidden">
              <div className="p-3 bg-white/60 text-emerald-800">
                <CircleDollarSign size={22} strokeWidth={1.5} />
              </div>
              <div>
                <p className="overline text-[10px]" style={{ color: "var(--ayu-muted)" }}>Completed Revenue</p>
                <p className="text-3xl font-display font-semibold text-[var(--ayu-primary)]">
                  ₹{completedRevenue.toLocaleString("en-IN")}
                </p>
              </div>
            </div>
          </div>

          {/* Filters and Search Bar */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
            <div className="flex-1 max-w-md relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ayu-muted)]" />
              <input
                type="text"
                data-testid="admin-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by customer name, email, city, order ID..."
                className="ayu-input pl-10 py-2.5 text-sm"
              />
            </div>

            <div className="flex items-center gap-3 self-start md:self-center">
              <SlidersHorizontal size={15} className="text-[var(--ayu-muted)]" />
              <span className="text-xs font-semibold uppercase tracking-wider text-[var(--ayu-muted)]">Status:</span>
              <select
                data-testid="admin-filter-status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="bg-transparent border-b border-[var(--ayu-border)] text-sm py-1 font-medium text-[var(--ayu-heading)] focus:outline-none focus:border-[var(--ayu-secondary)] rounded-none"
              >
                <option value="all">All Orders</option>
                <option value="inquiry_received">Inquiry Received</option>
                <option value="confirmed">Confirmed</option>
                <option value="shipped">Shipped</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Orders List / Table */}
          {loadingOrders ? (
            <div className="py-24 text-center text-sm text-[var(--ayu-muted)]">
              <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin mx-auto mb-3" style={{ borderColor: "var(--ayu-primary) transparent transparent transparent" }}></div>
              Retrieving clinic ledgers from Cloudflare D1...
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="border border-dashed border-[var(--ayu-border)] p-16 text-center bg-white">
              <p className="text-sm font-medium mb-1" style={{ color: "var(--ayu-heading)" }}>No matches found</p>
              <p className="text-xs opacity-75 max-w-sm mx-auto" style={{ color: "var(--ayu-muted)" }}>
                Try refining your search terms or adjusting the status filter to find the order records you are seeking.
              </p>
            </div>
          ) : (
            <div className="space-y-8" data-testid="admin-orders-list">
              {filteredOrders.map((order) => {
                const status = getStatusBadge(order.status);
                const orderDate = new Date(order.created_at).toLocaleString("en-IN", {
                  year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
                });

                return (
                  <div 
                    key={order.id} 
                    data-testid={`admin-order-card-${order.id}`}
                    className="bg-white border border-[var(--ayu-border)] shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
                  >
                    {/* Order Top Bar */}
                    <div className="px-6 py-4 bg-[var(--ayu-bg)] border-b border-[var(--ayu-border)] flex flex-wrap items-center justify-between gap-4">
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-[var(--ayu-heading)] flex items-center gap-2">
                          <span>Order Ledger ID:</span>
                          <span className="font-mono text-[10px] opacity-75">{order.id}</span>
                        </p>
                        <p className="text-[11px] flex items-center gap-1.5 opacity-75" style={{ color: "var(--ayu-muted)" }}>
                          <Calendar size={12} /> {orderDate}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {/* Status Dropdown Controller */}
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)]">Set Status:</span>
                          <select
                            value={order.status}
                            disabled={updatingId === order.id}
                            data-testid={`admin-status-select-${order.id}`}
                            onChange={(e) => updateStatus(order.id, e.target.value)}
                            className="bg-white border border-[var(--ayu-border)] text-xs py-1 px-2 font-medium text-[var(--ayu-heading)] rounded-none focus:outline-none focus:border-[var(--ayu-secondary)] disabled:opacity-50"
                          >
                            <option value="inquiry_received">Inquiry Received</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="shipped">Shipped</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </div>

                        <span className={`text-[10px] uppercase font-bold tracking-wider px-3 py-1 rounded-sm border ${status.bg} ${status.text} ${status.border}`}>
                          {status.label}
                        </span>
                      </div>
                    </div>

                    {/* Order Split Layout */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6">
                      {/* Left Side: Order Items */}
                      <div className="lg:col-span-7 space-y-4">
                        <p className="overline text-[10px]" style={{ color: "var(--ayu-muted)" }}>Apothecary Cart</p>
                        <div className="space-y-3">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm py-1 border-b border-[var(--ayu-bg)] last:border-b-0">
                              <div className="flex items-center gap-3">
                                {item.image ? (
                                  <img 
                                    src={item.image} 
                                    alt={item.name} 
                                    className="w-10 h-10 object-cover border border-[var(--ayu-border)]"
                                  />
                                ) : (
                                  <div className="w-10 h-10 bg-[var(--ayu-surface)] flex items-center justify-center text-xs">
                                    🌿
                                  </div>
                                )}
                                <div>
                                  <h4 className="font-medium text-[var(--ayu-heading)] leading-tight">{item.name}</h4>
                                  <p className="text-xs opacity-75" style={{ color: "var(--ayu-muted)" }}>
                                    ₹{item.price.toLocaleString("en-IN")} × {item.quantity}
                                  </p>
                                </div>
                              </div>
                              <span className="font-medium text-[var(--ayu-heading)]">
                                ₹{(item.price * item.quantity).toLocaleString("en-IN")}
                              </span>
                            </div>
                          ))}
                        </div>

                        <div className="pt-3 border-t border-[var(--ayu-border)] flex items-center justify-between">
                          <span className="text-xs font-semibold uppercase tracking-wider text-[var(--ayu-muted)]">Order Total</span>
                          <span className="font-display text-xl text-[var(--ayu-primary)] font-bold">
                            ₹{order.total.toLocaleString("en-IN")}
                          </span>
                        </div>
                      </div>

                      {/* Right Side: Shipping & Contact details */}
                      <div className="lg:col-span-5 bg-[var(--ayu-surface)]/40 p-5 border border-[var(--ayu-border)]/50 space-y-4 text-xs">
                        <p className="overline text-[10px]" style={{ color: "var(--ayu-muted)" }}>Customer Delivery Ledger</p>
                        
                        <div className="space-y-3 text-sm">
                          <div className="flex gap-2">
                            <User size={13} className="text-[var(--ayu-primary)] mt-0.5 shrink-0" />
                            <div>
                              <p className="text-[10px] overline tracking-wider text-[var(--ayu-muted)]">Recipient</p>
                              <p className="font-semibold text-[var(--ayu-heading)]">{order.customer_name}</p>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <Mail size={13} className="text-[var(--ayu-primary)] mt-0.5 shrink-0" />
                            <div>
                              <p className="text-[10px] overline tracking-wider text-[var(--ayu-muted)]">Email Link</p>
                              <p className="text-[var(--ayu-heading)]">{order.email}</p>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <Phone size={13} className="text-[var(--ayu-primary)] mt-0.5 shrink-0" />
                            <div>
                              <p className="text-[10px] overline tracking-wider text-[var(--ayu-muted)]">Phone Line</p>
                              <p className="text-[var(--ayu-heading)]">{order.phone}</p>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <MapPin size={13} className="text-[var(--ayu-primary)] mt-0.5 shrink-0" />
                            <div>
                              <p className="text-[10px] overline tracking-wider text-[var(--ayu-muted)]">Address Coordinates</p>
                              <p className="text-[var(--ayu-heading)] leading-relaxed">
                                {order.address},<br />
                                {order.city} - {order.pincode}
                              </p>
                            </div>
                          </div>
                        </div>

                        {order.notes && (
                          <div className="pt-2 border-t border-[var(--ayu-border)]">
                            <p className="text-[10px] overline tracking-wider text-[var(--ayu-muted)] mb-1">Enquiry Notes</p>
                            <p className="italic text-[var(--ayu-body)] bg-white/70 p-2.5 border border-[var(--ayu-border)]/50 leading-relaxed">
                              "{order.notes}"
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {activeTab === "products" && (
        <>
          {/* Product Header / Actions */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
            <div className="flex-1 max-w-md relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ayu-muted)]" />
              <input
                type="text"
                data-testid="admin-product-search"
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                placeholder="Search products by name, subcategory..."
                className="ayu-input pl-10 py-2.5 text-sm"
              />
            </div>

            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-3">
                <SlidersHorizontal size={15} className="text-[var(--ayu-muted)]" />
                <span className="text-xs font-semibold uppercase tracking-wider text-[var(--ayu-muted)]">Category:</span>
                <select
                  value={productFilterCategory}
                  onChange={(e) => setProductFilterCategory(e.target.value)}
                  className="bg-transparent border-b border-[var(--ayu-border)] text-sm py-1 font-medium text-[var(--ayu-heading)] focus:outline-none focus:border-[var(--ayu-secondary)] rounded-none"
                >
                  <option value="all">All Categories</option>
                  <option value="medicine">Medicines</option>
                  <option value="equipment">Equipment</option>
                </select>
              </div>

              <button 
                onClick={() => setIsAddingProduct(true)}
                data-testid="admin-add-product-btn"
                className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5"
              >
                <Plus size={14} /> Add Product
              </button>
            </div>
          </div>

          {/* Product Listing */}
          {loadingProducts ? (
            <div className="py-24 text-center text-sm text-[var(--ayu-muted)]">
              <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin mx-auto mb-3" style={{ borderColor: "var(--ayu-primary) transparent transparent transparent" }}></div>
              Retrieving product catalog from D1...
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="border border-dashed border-[var(--ayu-border)] p-16 text-center bg-white">
              <p className="text-sm font-medium mb-1" style={{ color: "var(--ayu-heading)" }}>No products found</p>
              <p className="text-xs opacity-75 max-w-sm mx-auto" style={{ color: "var(--ayu-muted)" }}>
                Try adjusting your search terms or create a new product to add to the inventory.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-products-list">
              {filteredProducts.map((product) => (
                <div 
                  key={product.id}
                  data-testid={`admin-product-card-${product.slug}`}
                  className="bg-white border border-[var(--ayu-border)] p-5 flex flex-col justify-between hover:shadow-md transition-all duration-300 relative overflow-hidden"
                >
                  <div className="grain absolute inset-0 pointer-events-none opacity-[0.02]"></div>
                  
                  {/* Pencil Edit Button */}
                  <button
                    type="button"
                    onClick={() => {
                      setEditingProduct({
                        id: product.id,
                        name: product.name,
                        category: product.category,
                        sub_category: product.sub_category || "",
                        short_description: product.short_description,
                        description: product.description,
                        price: product.price,
                        imageType: "url",
                        imageUrl: product.image,
                        imageFileBase64: "",
                        benefits: product.benefits && product.benefits.length > 0 ? product.benefits : [""],
                        in_stock: product.in_stock.toString()
                      });
                      setIsEditingProduct(true);
                    }}
                    className="absolute top-3 right-3 z-20 p-2 bg-white/80 hover:bg-white border border-[var(--ayu-border)] rounded-full text-[var(--ayu-primary)] hover:text-[var(--ayu-secondary)] transition-all shadow-sm"
                    title="Edit Product Parameters"
                  >
                    <Edit size={14} />
                  </button>

                  <div className="relative z-10">
                    {/* Image & Top Info */}
                    <div className="aspect-video w-full overflow-hidden mb-4 border border-[var(--ayu-border)] bg-[var(--ayu-bg)] flex items-center justify-center">
                      {product.image ? (
                        <img 
                          src={product.image} 
                          alt={product.name} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-3xl">🌿</span>
                      )}
                    </div>

                    <p className="text-[10px] overline tracking-wider text-[var(--ayu-secondary)] mb-1">
                      {product.category} {product.sub_category ? `• ${product.sub_category}` : ''}
                    </p>
                    <h3 className="font-display text-2xl text-[var(--ayu-heading)] font-semibold mb-2 leading-tight">{product.name}</h3>
                    <p className="text-xs text-[var(--ayu-body)] line-clamp-2 mb-4 leading-relaxed">{product.short_description}</p>
                  </div>

                  {/* Inline Edits */}
                  <div className="border-t border-[var(--ayu-border)] pt-4 mt-2 space-y-4 relative z-10">
                    {/* Cost adjust */}
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)]">Cost (INR):</span>
                      <div className="flex items-center gap-1 max-w-[120px]">
                        <span className="text-xs text-[var(--ayu-body)] font-medium">₹</span>
                        <input 
                          type="number" 
                          min="0"
                          step="1"
                          value={product.price}
                          onChange={(e) => handlePriceChange(product.id, e.target.value)}
                          data-testid={`admin-price-input-${product.slug}`}
                          className="bg-transparent border-b border-[var(--ayu-border)] text-xs text-right py-0.5 w-full font-semibold focus:outline-none focus:border-[var(--ayu-secondary)] rounded-none"
                        />
                      </div>
                    </div>

                    {/* Stock adjust */}
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)]">Stock Qty:</span>
                      <input 
                        type="number" 
                        min="0"
                        step="1"
                        value={product.in_stock}
                        onChange={(e) => handleStockChange(product.id, e.target.value)}
                        data-testid={`admin-stock-input-${product.slug}`}
                        className="bg-transparent border-b border-[var(--ayu-border)] text-xs text-right py-0.5 w-[80px] font-semibold focus:outline-none focus:border-[var(--ayu-secondary)] rounded-none"
                      />
                    </div>

                    {/* Action */}
                    <button 
                      onClick={() => saveProductChanges(product)}
                      disabled={savingProductId === product.id}
                      data-testid={`admin-save-btn-${product.slug}`}
                      className="btn-primary w-full text-[11px] py-2.5 px-3 mt-2 justify-center"
                    >
                      {savingProductId === product.id ? "Saving Changes..." : "Save Product Changes"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Add Product Modal Overlay */}
      {isAddingProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="admin-add-product-modal">
          <div className="bg-[var(--ayu-bg)] grain border border-[var(--ayu-border)] max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 relative shadow-2xl rounded-sm">
            <button 
              onClick={() => setIsAddingProduct(false)}
              className="absolute top-4 right-4 text-[var(--ayu-muted)] hover:text-[var(--ayu-heading)] transition-colors"
            >
              <X size={20} />
            </button>

            <h2 className="font-display text-3xl text-[var(--ayu-heading)] mb-6 pb-2 border-b border-[var(--ayu-border)]">
              Add New Apothecary Product
            </h2>

            <form onSubmit={handleAddProductSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block mb-1">Product Name *</label>
                  <input 
                    type="text" 
                    required
                    placeholder="e.g. Ashwagandha Churna"
                    value={newProduct.name}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="new-product-name"
                    className="ayu-input py-2 text-sm"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block mb-1">Category *</label>
                  <select
                    value={newProduct.category}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, category: e.target.value }))}
                    data-testid="new-product-category"
                    className="ayu-input py-2 text-sm bg-transparent"
                  >
                    <option value="medicine">Medicine</option>
                    <option value="equipment">Equipment</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Sub category */}
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block mb-1">Sub Category</label>
                  <input 
                    type="text" 
                    placeholder="e.g. Rasayana, Digestive, Panchakarma"
                    value={newProduct.sub_category}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, sub_category: e.target.value }))}
                    data-testid="new-product-subcategory"
                    className="ayu-input py-2 text-sm"
                  />
                </div>

                {/* Price */}
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block mb-1">Price (INR) *</label>
                  <input 
                    type="number" 
                    required
                    min="0"
                    step="1"
                    placeholder="449"
                    value={newProduct.price}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, price: e.target.value }))}
                    data-testid="new-product-price"
                    className="ayu-input py-2 text-sm"
                  />
                </div>
              </div>

              {/* Short Description */}
              <div>
                <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block mb-1">Short Description *</label>
                <input 
                  type="text" 
                  required
                  placeholder="e.g. Premium adaptogenic root powder for stress and vitality."
                  value={newProduct.short_description}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, short_description: e.target.value }))}
                  data-testid="new-product-short-desc"
                  className="ayu-input py-2 text-sm"
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block mb-1">Full Description *</label>
                <textarea 
                  required
                  rows={3}
                  placeholder="Detailed description of benefits, usage, and traditional specs..."
                  value={newProduct.description}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, description: e.target.value }))}
                  data-testid="new-product-desc"
                  className="ayu-input py-2 text-sm"
                />
              </div>

              {/* Image Source Selection */}
              <div className="space-y-3">
                <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block">Product Image *</label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setNewProduct(prev => ({ ...prev, imageType: "url" }))}
                    className={`text-xs py-1.5 px-3 border transition-all ${
                      newProduct.imageType === "url"
                        ? "border-[var(--ayu-primary)] bg-[var(--ayu-primary)] text-white"
                        : "border-[var(--ayu-border)] text-[var(--ayu-muted)] hover:text-[var(--ayu-heading)]"
                    }`}
                  >
                    Image URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewProduct(prev => ({ ...prev, imageType: "file" }))}
                    className={`text-xs py-1.5 px-3 border transition-all ${
                      newProduct.imageType === "file"
                        ? "border-[var(--ayu-primary)] bg-[var(--ayu-primary)] text-white"
                        : "border-[var(--ayu-border)] text-[var(--ayu-muted)] hover:text-[var(--ayu-heading)]"
                    }`}
                  >
                    Upload Local File
                  </button>
                </div>

                {newProduct.imageType === "url" ? (
                  <div>
                    <input 
                      type="url" 
                      required={newProduct.imageType === "url"}
                      placeholder="e.g. https://images.pexels.com/... or /images/ashwagandha.png"
                      value={newProduct.imageUrl}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, imageUrl: e.target.value }))}
                      data-testid="new-product-image-url"
                      className="ayu-input py-2 text-sm"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-4 border-b border-[var(--ayu-border)]/50 pb-3 pt-1">
                    <input 
                      type="file" 
                      accept="image/*"
                      required={newProduct.imageType === "file" && !newProduct.imageFileBase64}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setNewProduct(prev => ({ ...prev, imageFileBase64: reader.result }));
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      data-testid="new-product-image-file"
                      className="text-xs text-[var(--ayu-body)] focus:outline-none"
                    />
                    {newProduct.imageFileBase64 && (
                      <img 
                        src={newProduct.imageFileBase64} 
                        alt="Preview" 
                        className="w-12 h-12 object-cover border border-[var(--ayu-border)]"
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Benefits List */}
              <div className="space-y-3">
                <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block">Key Benefits</label>
                {newProduct.benefits.map((benefit, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <input 
                      type="text" 
                      placeholder={`Benefit #${index + 1}`}
                      value={benefit}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewProduct(prev => {
                          const copy = [...prev.benefits];
                          copy[index] = val;
                          return { ...prev, benefits: copy };
                        });
                      }}
                      data-testid={`new-product-benefit-${index}`}
                      className="ayu-input py-1 text-sm flex-1"
                    />
                    {newProduct.benefits.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setNewProduct(prev => ({ 
                          ...prev, 
                          benefits: prev.benefits.filter((_, i) => i !== index) 
                        }))}
                        className="text-red-700 hover:text-red-950 p-1 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setNewProduct(prev => ({ ...prev, benefits: [...prev.benefits, ""] }))}
                  className="text-xs text-[var(--ayu-secondary)] hover:text-[var(--ayu-secondary-hover)] font-medium flex items-center gap-1 mt-1 transition-colors"
                >
                  <Plus size={12} /> Add Benefit
                </button>
              </div>

              {/* Form Actions */}
              {/* Stock Count */}
              <div>
                <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block mb-1">Initial Stock Quantity *</label>
                <input 
                  type="number" 
                  required
                  min="0"
                  step="1"
                  placeholder="10"
                  value={newProduct.in_stock}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, in_stock: e.target.value }))}
                  data-testid="new-product-stock"
                  className="ayu-input py-2 text-sm"
                />
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-4 pt-4 border-t border-[var(--ayu-border)]">
                <button 
                  type="button" 
                  onClick={() => setIsAddingProduct(false)}
                  className="btn-secondary text-xs py-2 px-4"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={submittingNewProduct}
                  data-testid="new-product-submit"
                  className="btn-primary text-xs py-2 px-6 flex items-center gap-2 disabled:opacity-50"
                >
                  {submittingNewProduct ? "Creating..." : "Create Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Product Modal Overlay */}
      {isEditingProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="admin-edit-product-modal">
          <div className="bg-[var(--ayu-bg)] grain border border-[var(--ayu-border)] max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 relative shadow-2xl rounded-sm">
            <button 
              type="button"
              onClick={() => setIsEditingProduct(false)}
              className="absolute top-4 right-4 text-[var(--ayu-muted)] hover:text-[var(--ayu-heading)] transition-colors"
            >
              <X size={20} />
            </button>

            <h2 className="font-display text-3xl text-[var(--ayu-heading)] mb-6 pb-2 border-b border-[var(--ayu-border)]">
              Edit Product: {editingProduct.name}
            </h2>

            <form onSubmit={handleEditProductSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block mb-1">Product Name *</label>
                  <input 
                    type="text" 
                    required
                    placeholder="e.g. Ashwagandha Churna"
                    value={editingProduct.name}
                    onChange={(e) => setEditingProduct(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="edit-product-name"
                    className="ayu-input py-2 text-sm"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block mb-1">Category *</label>
                  <select
                    value={editingProduct.category}
                    onChange={(e) => setEditingProduct(prev => ({ ...prev, category: e.target.value }))}
                    data-testid="edit-product-category"
                    className="ayu-input py-2 text-sm bg-transparent"
                  >
                    <option value="medicine">Medicine</option>
                    <option value="equipment">Equipment</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Sub category */}
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block mb-1">Sub Category</label>
                  <input 
                    type="text" 
                    placeholder="e.g. Rasayana, Digestive, Panchakarma"
                    value={editingProduct.sub_category}
                    onChange={(e) => setEditingProduct(prev => ({ ...prev, sub_category: e.target.value }))}
                    data-testid="edit-product-subcategory"
                    className="ayu-input py-2 text-sm"
                  />
                </div>

                {/* Price */}
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block mb-1">Price (INR) *</label>
                  <input 
                    type="number" 
                    required
                    min="0"
                    step="1"
                    placeholder="449"
                    value={editingProduct.price}
                    onChange={(e) => setEditingProduct(prev => ({ ...prev, price: e.target.value }))}
                    data-testid="edit-product-price"
                    className="ayu-input py-2 text-sm"
                  />
                </div>
              </div>

              {/* Short Description */}
              <div>
                <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block mb-1">Short Description *</label>
                <input 
                  type="text" 
                  required
                  placeholder="e.g. Premium adaptogenic root powder for stress and vitality."
                  value={editingProduct.short_description}
                  onChange={(e) => setEditingProduct(prev => ({ ...prev, short_description: e.target.value }))}
                  data-testid="edit-product-short-desc"
                  className="ayu-input py-2 text-sm"
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block mb-1">Full Description *</label>
                <textarea 
                  required
                  rows={3}
                  placeholder="Detailed description of benefits, usage, and traditional specs..."
                  value={editingProduct.description}
                  onChange={(e) => setEditingProduct(prev => ({ ...prev, description: e.target.value }))}
                  data-testid="edit-product-desc"
                  className="ayu-input py-2 text-sm"
                />
              </div>

              {/* Image Source Selection */}
              <div className="space-y-3">
                <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block">Product Image *</label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setEditingProduct(prev => ({ ...prev, imageType: "url" }))}
                    className={`text-xs py-1.5 px-3 border transition-all ${
                      editingProduct.imageType === "url"
                        ? "border-[var(--ayu-primary)] bg-[var(--ayu-primary)] text-white"
                        : "border-[var(--ayu-border)] text-[var(--ayu-muted)] hover:text-[var(--ayu-heading)]"
                    }`}
                  >
                    Image URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingProduct(prev => ({ ...prev, imageType: "file" }))}
                    className={`text-xs py-1.5 px-3 border transition-all ${
                      editingProduct.imageType === "file"
                        ? "border-[var(--ayu-primary)] bg-[var(--ayu-primary)] text-white"
                        : "border-[var(--ayu-border)] text-[var(--ayu-muted)] hover:text-[var(--ayu-heading)]"
                    }`}
                  >
                    Upload Local File
                  </button>
                </div>

                {editingProduct.imageType === "url" ? (
                  <div>
                    <input 
                      type="url" 
                      required={editingProduct.imageType === "url"}
                      placeholder="e.g. https://images.pexels.com/... or /images/ashwagandha.png"
                      value={editingProduct.imageUrl}
                      onChange={(e) => setEditingProduct(prev => ({ ...prev, imageUrl: e.target.value }))}
                      data-testid="edit-product-image-url"
                      className="ayu-input py-2 text-sm"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-4 border-b border-[var(--ayu-border)]/50 pb-3 pt-1">
                    <input 
                      type="file" 
                      accept="image/*"
                      required={editingProduct.imageType === "file" && !editingProduct.imageFileBase64}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setEditingProduct(prev => ({ ...prev, imageFileBase64: reader.result }));
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      data-testid="edit-product-image-file"
                      className="text-xs text-[var(--ayu-body)] focus:outline-none"
                    />
                    {editingProduct.imageFileBase64 && (
                      <img 
                        src={editingProduct.imageFileBase64} 
                        alt="Preview" 
                        className="w-12 h-12 object-cover border border-[var(--ayu-border)]"
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Benefits List */}
              <div className="space-y-3">
                <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block">Key Benefits</label>
                {editingProduct.benefits.map((benefit, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <input 
                      type="text" 
                      placeholder={`Benefit #${index + 1}`}
                      value={benefit}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEditingProduct(prev => {
                          const copy = [...prev.benefits];
                          copy[index] = val;
                          return { ...prev, benefits: copy };
                        });
                      }}
                      data-testid={`edit-product-benefit-${index}`}
                      className="ayu-input py-1 text-sm flex-1"
                    />
                    {editingProduct.benefits.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setEditingProduct(prev => ({ 
                          ...prev, 
                          benefits: prev.benefits.filter((_, i) => i !== index) 
                        }))}
                        className="text-red-700 hover:text-red-950 p-1 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setEditingProduct(prev => ({ ...prev, benefits: [...prev.benefits, ""] }))}
                  className="text-xs text-[var(--ayu-secondary)] hover:text-[var(--ayu-secondary-hover)] font-medium flex items-center gap-1 mt-1 transition-colors"
                >
                  <Plus size={12} /> Add Benefit
                </button>
              </div>

              {/* Stock Count */}
              <div>
                <label className="text-[10px] uppercase font-bold tracking-wider text-[var(--ayu-muted)] block mb-1">Stock Quantity *</label>
                <input 
                  type="number" 
                  required
                  min="0"
                  step="1"
                  placeholder="10"
                  value={editingProduct.in_stock}
                  onChange={(e) => setEditingProduct(prev => ({ ...prev, in_stock: e.target.value }))}
                  data-testid="edit-product-stock"
                  className="ayu-input py-2 text-sm"
                />
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-4 pt-4 border-t border-[var(--ayu-border)]">
                <button 
                  type="button" 
                  onClick={() => setIsEditingProduct(false)}
                  className="btn-secondary text-xs py-2 px-4"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={submittingEditProduct}
                  data-testid="edit-product-submit"
                  className="btn-primary text-xs py-2 px-6 flex items-center gap-2 disabled:opacity-50"
                >
                  {submittingEditProduct ? "Updating..." : "Update Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
